import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { QrCode } from "lucide-react";
import { useI18n } from "@/lib/i18n/i18n";

let camSeq = 0;
let camChain = Promise.resolve();

const CameraFeed = ({ onDecode }) => {
  const [id] = useState(() => `cam-feed-${++camSeq}`);
  const cb = useRef(onDecode);
  cb.current = onDecode;
  const [error, setError] = useState(null);
  const { t } = useI18n();

  useEffect(() => {
    const q = new Html5Qrcode(id, { verbose: false });
    let started = false;
    camChain = camChain
      .then(() => q.start(
        { facingMode: "environment" },
        { fps: 12, qrbox: (w, h) => { const s = Math.max(140, Math.floor(Math.min(w, h) * 0.75)); return { width: s, height: s }; } },
        (text) => cb.current(text),
        () => {},
      ))
      .then(() => { started = true; })
      .catch(() => setError(true));
    return () => {
      camChain = camChain.then(() => (started ? q.stop().then(() => q.clear()) : null)).catch(() => {});
    };
  }, [id]);

  return (
    <div className="absolute inset-0">
      <div id={id} className="cam-feed absolute inset-0" />
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/90 p-6 text-center text-sm text-white/80" data-testid="camera-error">{t("No se ha podido acceder a la cámara. Usa «Simular lectura QR».")}</div>
      )}
    </div>
  );
};

const Corner = ({ pos, active }) => {
  const map = {
    tl: "left-5 top-5 border-l-[3px] border-t-[3px] rounded-tl-2xl",
    tr: "right-5 top-5 border-r-[3px] border-t-[3px] rounded-tr-2xl",
    bl: "left-5 bottom-5 border-l-[3px] border-b-[3px] rounded-bl-2xl",
    br: "right-5 bottom-5 border-r-[3px] border-b-[3px] rounded-br-2xl",
  };
  return <span className={`pointer-events-none absolute h-12 w-12 transition-colors duration-200 ${map[pos]} ${active ? "border-emerald-400" : "border-white/90"}`} />;
};

export const ScanViewport = ({ phase, cameraOn, onDecode, className = "" }) => {
  const scanning = phase === "scanning";
  const { t } = useI18n();
  return (
    <div className={`relative aspect-square w-full overflow-hidden rounded-[28px] bg-slate-900 ${className}`} data-testid="scan-viewport">
      {cameraOn ? (
        <CameraFeed onDecode={onDecode} />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-[radial-gradient(circle_at_center,#1e293b_0%,#0b1220_70%)]">
          <QrCode className={`h-1/3 w-1/3 transition-colors duration-300 ${scanning ? "text-emerald-300/50" : "text-white/10"}`} strokeWidth={1.2} />
        </div>
      )}
      {["tl", "tr", "bl", "br"].map((p) => <Corner key={p} pos={p} active={scanning} />)}
      <div className={`scan-beam ${scanning ? "fast" : ""}`} />
      {scanning && <div className="scan-flash absolute inset-0 bg-emerald-300/20" />}
      <div className="absolute bottom-7 left-0 right-0 flex justify-center">
        <span className="flex items-center gap-2 rounded-full bg-black/40 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/80 backdrop-blur" data-testid="scan-viewport-status">
          <span className={`h-1.5 w-1.5 rounded-full ${scanning ? "bg-emerald-400" : "animate-pulse bg-rose-400"}`} />
          {t(scanning ? "Leyendo…" : cameraOn ? "Cámara activa" : "Lector activo")}
        </span>
      </div>
    </div>
  );
};
